import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  CSidebar,
  CSidebarBrand,
  CSidebarNav,
  CSidebarMinimizer,
  //CSidebarNavDropdown,
  CSidebarNavItem,
  CSidebarNavDropdown,
} from "@coreui/react";

//import CIcon from '@coreui/icons-react';

import {
  minimizeDashboard,
  showDashboard,
} from "../../redux/actions/dashboard";
import { RootState } from "../../store";

// sidebar nav config
import { getRouteName, getRoutePath } from "../../routes";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  ACCOUNTING_ENTRIES_RETRIEVE,
  AUTO_INVOICES_RETRIEVE,
  CREDIT_NOTES_CREATE,
  CREDIT_NOTES_RETRIEVE,
  CREDIT_NOTES_UPDATE,
  DEBIT_NOTES_CREATE,
  DEBIT_NOTES_RETRIEVE,
  DEBIT_NOTES_UPDATE,
  DISABLEMENTS_RETRIEVE,
  INVOICES_CREATE,
  INVOICES_RETRIEVE,
  INVOICES_UPDATE,
  RECEIPTS_CREATE,
  RECEIPTS_RETRIEVE,
  RECEIPTS_UPDATE,
  RECEIVED_DOCUMENTS_RETRIEVE,
  REMISSION_NOTES_CREATE,
  REMISSION_NOTES_RETRIEVE,
  REMISSION_NOTES_UPDATE,
  SUPPLIERS_RETRIEVE,
  TAX_PAYER_SETTINGS_UPDATE,
  CONNECTIVITY_MANAGE,
  USERS_RETRIEVE,
  IVA_RETRIEVE,
} from "../../auth/permissions";
import HasAnyPermissionComponent from "../permissions/HasAnyPermissionComponent";
import DemoDeveloperModeNavItem from "./DemoDeveloperModeNavItem";
import ThemeToggle from "./ThemeToggle";
import RucConsultModal from "../ruc/RucConsultModal";
import { useNotificationSettingsAccess } from "../../hooks/notification-settings-access";
import { SIFEN_INCIDENT_VIEW_PERMISSIONS } from "../../models/sifen-incident";
import { SHOW_SIFEN_INCIDENTS } from "../../config/features";

const CLIENT_NAV_PERMISSIONS = [
  INVOICES_RETRIEVE, INVOICES_CREATE, INVOICES_UPDATE,
  CREDIT_NOTES_RETRIEVE, CREDIT_NOTES_CREATE, CREDIT_NOTES_UPDATE,
  DEBIT_NOTES_RETRIEVE, DEBIT_NOTES_CREATE, DEBIT_NOTES_UPDATE,
  REMISSION_NOTES_RETRIEVE, REMISSION_NOTES_CREATE, REMISSION_NOTES_UPDATE,
  RECEIPTS_RETRIEVE, RECEIPTS_CREATE, RECEIPTS_UPDATE,
];
const CATALOG_NAV_PERMISSIONS = [INVOICES_RETRIEVE, INVOICES_CREATE, INVOICES_UPDATE];
const SUPPLIER_NAV_PERMISSIONS = [RECEIVED_DOCUMENTS_RETRIEVE, SUPPLIERS_RETRIEVE];
const CONTACT_NAV_PERMISSIONS = [...CLIENT_NAV_PERMISSIONS, ...SUPPLIER_NAV_PERMISSIONS];

const Sidebar = () => {
  const history = useHistory();
  const [showRucConsult, setShowRucConsult] = useState(false);
  const { ready: navigationReady, whatsappAllowed } =
    useNotificationSettingsAccess();
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );
  const dispatch = useDispatch();
  const show = useSelector((state: RootState) => state.dashboard.sidebarShow);
  const minimize = useSelector(
    (state: RootState) => state.dashboard.sidebarMinimize,
  );
  const connectivityAccess = useSelector(
    (state: RootState) => state.taxPayerParameters.data.connectivityAccess,
  );
  const accountingAccess = useSelector(
    (state: RootState) => state.taxPayerParameters.data.accountingAccess,
  );
  const aiAccess = useSelector(
    (state: RootState) => state.taxPayerParameters.data.aiAccess,
  );
  const aiSettingsManageable = useSelector(
    (state: RootState) => state.taxPayerParameters.data.aiSettingsManageable,
  );
  const taxPayerPermissions = useSelector(
    (state: RootState) => state.taxPayerPermissions.permissions,
  );

  // The server resolves who may open the AI screen. It is an item of its own
  // for every company, never inside Configuración: a demo user never sees
  // Configuración, and one place for everybody is easier to point at.
  const showAiSettings = aiAccess === true && aiSettingsManageable;
  const settingsAllowed =
    taxPayerPermissions !== undefined &&
    taxPayerPermissions.indexOf(TAX_PAYER_SETTINGS_UPDATE) >= 0;

  const isConfigurationNavPage = () => {
    return (
      window.location.pathname === getRoutePath("regimeTypes") ||
      window.location.pathname === getRoutePath("economicActivities") ||
      window.location.pathname === getRoutePath("taxPayer") ||
      window.location.pathname === getRoutePath("stamps") ||
      window.location.pathname === getRoutePath("establishments") ||
      window.location.pathname === getRoutePath("dispatchPoints") ||
      window.location.pathname === getRoutePath("taxPayerAssets") ||
      window.location.pathname === getRoutePath("taxPayerEmailSettings") ||
      window.location.pathname === getRoutePath("taxPayerWhatsappSettings") ||
      window.location.pathname === getRoutePath("publicPortal")
    );
  };

  const isConnectivityNavPage = () => {
    return (
      window.location.pathname === getRoutePath("taxPayerApiKeys") ||
      window.location.pathname === getRoutePath("webhookEndpoints")
    );
  };

  const isContactsNavPage = (pathname: string) => {
    return ["clients", "suppliers", "catalogItems"].some(
      (route) => pathname === getRoutePath(route),
    );
  };

  const isAccountingNavPage = (pathname: string) => {
    return (
      pathname === getRoutePath("iva") ||
      pathname === getRoutePath("accountingReports") ||
      pathname === getRoutePath("accountingAccounts") ||
      pathname === getRoutePath("accountingEntries") ||
      pathname === getRoutePath("accountingReportsHistory")
    );
  };

  const [showConfigurationNav, setShowConfigurationNav] = useState(
    isConfigurationNavPage(),
  );

  const [showConnectivityNav, setShowConnectivityNav] = useState(
    isConnectivityNavPage(),
  );

  const [showContactsNav, setShowContactsNav] = useState(
    isContactsNavPage(history.location.pathname),
  );

  const [showAccountingNav, setShowAccountingNav] = useState(
    isAccountingNavPage(history.location.pathname),
  );

  useEffect(() => {
    return history.listen((location) => {
      setShowConfigurationNav(isConfigurationNavPage());
      setShowContactsNav(isContactsNavPage(location.pathname));
      setShowAccountingNav(isAccountingNavPage(location.pathname));
      setShowConnectivityNav(isConnectivityNavPage());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <CSidebar
      show={show}
      onShowChange={(val: boolean) => {
        dispatch(showDashboard(val));
        window.localStorage.setItem("sidebarShow", val ? "true" : "false");
      }}
      minimize={minimize}
      onMinimizeChange={(val: boolean) => {
        dispatch(minimizeDashboard(!val));
        window.localStorage.setItem("sidebarMinimize", !val ? "true" : "false");
      }}
      dropdownMode={"noAction"}
    >
      <CSidebarBrand className="d-md-down-none" to="/">
        {minimize ? (
          <img
            src="/smart-logo-no-text-white.webp"
            alt="SmartDoc"
            style={{ height: "32px", width: "32px", objectFit: "contain" }}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img
              src="/smart-logo-no-text-white.webp"
              alt="SmartDoc"
              style={{ height: "32px", width: "32px", objectFit: "contain" }}
            />
            <span
              style={{
                fontWeight: "bold",
                fontSize: "1.2rem",
                color: "#ffffff",
              }}
            >
              SmartDoc
            </span>
          </div>
        )}
      </CSidebarBrand>
      <CSidebarNav aria-busy={!navigationReady}>
        {navigationReady ? (
          <>
            <CSidebarNavItem
              name={getRouteName("dashboard")}
              to={getRoutePath("dashboard")}
              icon={"cil-home"}
            ></CSidebarNavItem>
            {/*<CSidebarNavItem
          name={getRouteName("users")}
          to={getRoutePath("users")}
          icon={"cil-people"}
        ></CSidebarNavItem>*/}
            {/*<CCreateElement
          items={navigation}
          components={{
            CSidebarNavDivider,
            CSidebarNavDropdown,
            CSidebarNavItem,
            CSidebarNavTitle,
          }}
        />*/}
            <PermissionRequiredComponent permissionName={INVOICES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("invoices")}
                to={getRoutePath("invoices")}
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-file-invoice"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={CREDIT_NOTES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("creditNotes")}
                to={getRoutePath("creditNotes")}
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-file-circle-minus"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={DEBIT_NOTES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("debitNotes")}
                to={getRoutePath("debitNotes")}
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-file-circle-plus"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={REMISSION_NOTES_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("remissionNotes")}
                to={getRoutePath("remissionNotes")}
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-truck-fast"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={AUTO_INVOICES_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("autoInvoices")}
                to={getRoutePath("autoInvoices")}
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-file-invoice-dollar"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={RECEIPTS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("receipts")}
                to={getRoutePath("receipts")}
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-receipt"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={RECEIVED_DOCUMENTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("receivedDocuments")}
                to={getRoutePath("receivedDocuments")}
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-file-import"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={DISABLEMENTS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("disablements")}
                to={getRoutePath("disablements")}
                icon={<i className="c-sidebar-nav-icon fa-solid fa-ban"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            {taxPayer.id !== undefined && (
              <CSidebarNavItem
                name="Consulta RUC"
                title="Consulta RUC"
                icon={
                  <i className="c-sidebar-nav-icon fa-solid fa-magnifying-glass" />
                }
                onClick={() => {
                  if (window.innerWidth < 992) dispatch(showDashboard(false));
                  setShowRucConsult(true);
                }}
              />
            )}
            {SHOW_SIFEN_INCIDENTS && (
              <HasAnyPermissionComponent permissionNames={SIFEN_INCIDENT_VIEW_PERMISSIONS}>
                <CSidebarNavItem
                  name={getRouteName("sifenIncidents")}
                  to={getRoutePath("sifenIncidents")}
                  icon={<i className="c-sidebar-nav-icon fa fa-flag"></i>}
                />
              </HasAnyPermissionComponent>
            )}
            {showRucConsult &&
              taxPayer.id !== undefined &&
              createPortal(
                <RucConsultModal
                  key={taxPayer.id}
                  taxPayerId={taxPayer.id}
                  onClose={() => setShowRucConsult(false)}
                />,
                document.body,
              )}
            <HasAnyPermissionComponent permissionNames={CONTACT_NAV_PERMISSIONS}>
              <CSidebarNavDropdown
                name="Contactos y catálogo"
                icon={<i className="c-sidebar-nav-icon fa fa-address-book"></i>}
                show={showContactsNav}
              >
                <HasAnyPermissionComponent permissionNames={CLIENT_NAV_PERMISSIONS}>
                  <CSidebarNavItem
                    name={getRouteName("clients")}
                    to={getRoutePath("clients")}
                    icon={"cil-people"}
                  />
                </HasAnyPermissionComponent>
                <HasAnyPermissionComponent permissionNames={SUPPLIER_NAV_PERMISSIONS}>
                  <CSidebarNavItem
                    name={getRouteName("suppliers")}
                    to={getRoutePath("suppliers")}
                    icon={<i className="c-sidebar-nav-icon fa fa-truck"></i>}
                  />
                </HasAnyPermissionComponent>
                <HasAnyPermissionComponent permissionNames={CATALOG_NAV_PERMISSIONS}>
                  <CSidebarNavItem
                    name={getRouteName("catalogItems")}
                    to={getRoutePath("catalogItems")}
                    icon={<i className="c-sidebar-nav-icon fa fa-tags"></i>}
                  />
                </HasAnyPermissionComponent>
              </CSidebarNavDropdown>
            </HasAnyPermissionComponent>
            <PermissionRequiredComponent permissionName={USERS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("users")}
                to={getRoutePath("users")}
                icon={<i className="c-sidebar-nav-icon fa fa-users"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <HasAnyPermissionComponent
              permissionNames={[
                INVOICES_RETRIEVE,
                CREDIT_NOTES_RETRIEVE,
                DEBIT_NOTES_RETRIEVE,
                REMISSION_NOTES_RETRIEVE,
                AUTO_INVOICES_RETRIEVE,
                RECEIPTS_RETRIEVE,
              ]}
            >
              <CSidebarNavItem
                name={getRouteName("dashboard2")}
                to={getRoutePath("dashboard2")}
                icon={<i className="c-sidebar-nav-icon fa fa-chart-bar"></i>}
              ></CSidebarNavItem>
            </HasAnyPermissionComponent>
            <HasAnyPermissionComponent
              permissionNames={accountingAccess === true
                ? [IVA_RETRIEVE, ACCOUNTING_ENTRIES_RETRIEVE]
                : [IVA_RETRIEVE]}
            >
              <CSidebarNavDropdown
                name="Contabilidad"
                icon={<i className="c-sidebar-nav-icon fa fa-calculator"></i>}
                show={showAccountingNav}
              >
                <PermissionRequiredComponent permissionName={IVA_RETRIEVE}>
                  <CSidebarNavItem
                    name={getRouteName("iva")}
                    to={getRoutePath("iva")}
                    icon={<i className="c-sidebar-nav-icon fa-solid fa-percent"></i>}
                  />
                </PermissionRequiredComponent>
                {accountingAccess === true && (
                  <PermissionRequiredComponent permissionName={ACCOUNTING_ENTRIES_RETRIEVE}>
                    <CSidebarNavItem
                      name={getRouteName("accountingEntries")}
                      to={getRoutePath("accountingEntries")}
                      icon={<i className="c-sidebar-nav-icon fa-solid fa-book-open"></i>}
                    />
                    <CSidebarNavItem
                      name={getRouteName("accountingAccounts")}
                      to={getRoutePath("accountingAccounts")}
                      icon={<i className="c-sidebar-nav-icon fa-solid fa-scale-balanced"></i>}
                    />
                    <CSidebarNavItem
                      name={getRouteName("accountingReports")}
                      to={getRoutePath("accountingReports")}
                      icon={<i className="c-sidebar-nav-icon fa-solid fa-chart-line"></i>}
                    />
                    <CSidebarNavItem
                      name={getRouteName("accountingReportsHistory")}
                      to={getRoutePath("accountingReportsHistory")}
                      icon={<i className="c-sidebar-nav-icon fa-solid fa-clock-rotate-left"></i>}
                    />
                  </PermissionRequiredComponent>
                )}
              </CSidebarNavDropdown>
            </HasAnyPermissionComponent>
            {showAiSettings && (
              <CSidebarNavItem
                name={getRouteName("aiSettings")}
                to={getRoutePath("aiSettings")}
                icon={<i className="c-sidebar-nav-icon fa-solid fa-robot"></i>}
              ></CSidebarNavItem>
            )}
            {taxPayer.isDemo && taxPayer.id !== undefined && (
              <DemoDeveloperModeNavItem
                key={taxPayer.id}
                taxPayerId={taxPayer.id}
              />
            )}
            {connectivityAccess !== false && (
              <PermissionRequiredComponent permissionName={CONNECTIVITY_MANAGE}>
                <CSidebarNavDropdown
                  name="Conectividad"
                  icon={
                    <i className="c-sidebar-nav-icon fa  fa-tower-broadcast"></i>
                  }
                  show={showConnectivityNav}
                >
                  <CSidebarNavItem
                    name={getRouteName("taxPayerApiKeys")}
                    to={getRoutePath("taxPayerApiKeys")}
                    icon={<i className="c-sidebar-nav-icon fa fa-key"></i>}
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("webhookEndpoints")}
                    to={getRoutePath("webhookEndpoints")}
                    icon={
                      <i className="c-sidebar-nav-icon fa fa-circle-nodes"></i>
                    }
                  ></CSidebarNavItem>
                </CSidebarNavDropdown>
              </PermissionRequiredComponent>
            )}
            {settingsAllowed && (
              <CSidebarNavDropdown
                name="Configuración"
                icon={<i className="c-sidebar-nav-icon fa fa-gear"></i>}
                show={showConfigurationNav}
              >
                <PermissionRequiredComponent
                  permissionName={TAX_PAYER_SETTINGS_UPDATE}
                >
                  <CSidebarNavItem
                    name={getRouteName("publicPortal")}
                    to={getRoutePath("publicPortal")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-share-nodes"></i>
                    }
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("regimeTypes")}
                    to={getRoutePath("regimeTypes")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-tags"></i>
                    }
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("economicActivities")}
                    to={getRoutePath("economicActivities")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-briefcase"></i>
                    }
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("stamps")}
                    to={getRoutePath("stamps")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-stamp"></i>
                    }
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("establishments")}
                    to={getRoutePath("establishments")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-store"></i>
                    }
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("dispatchPoints")}
                    to={getRoutePath("dispatchPoints")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-location-dot"></i>
                    }
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("taxPayer")}
                    to={getRoutePath("taxPayer")}
                    icon={"company-icon"}
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("taxPayerAssets")}
                    to={getRoutePath("taxPayerAssets")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-images"></i>
                    }
                  ></CSidebarNavItem>
                  <CSidebarNavItem
                    name={getRouteName("taxPayerEmailSettings")}
                    to={getRoutePath("taxPayerEmailSettings")}
                    icon={
                      <i className="c-sidebar-nav-icon fa-solid fa-envelope" />
                    }
                  />
                  {whatsappAllowed && (
                    <CSidebarNavItem
                      name={getRouteName("taxPayerWhatsappSettings")}
                      to={getRoutePath("taxPayerWhatsappSettings")}
                      icon={
                        <i className="c-sidebar-nav-icon fa-brands fa-whatsapp" />
                      }
                    />
                  )}
                </PermissionRequiredComponent>
              </CSidebarNavDropdown>
            )}
          </>
        ) : (
          <span className="p-3 text-center" role="status">
            Cargando navegación...
          </span>
        )}
      </CSidebarNav>
      <ThemeToggle placement="sidebar" />
      <CSidebarMinimizer className="c-d-md-down-none" />
    </CSidebar>
  );
};

export default Sidebar;
